import { Outlet } from 'react-router-dom';
import { TopBar, Navigation, Frame } from '@shopify/polaris';
import {
  HomeIcon,
  ProductIcon,
  CollectionIcon,
  OrderIcon,
  PersonIcon,
  NoteIcon,
  SettingsIcon,
  ExitIcon,
} from '@shopify/polaris-icons';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { inquiryService } from '../../services/inquiry.service';

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [userMenuActive, setUserMenuActive] = useState(false);
  // Live count of NEW inquiries for the nav badge. Inquiries have no "read"
  // flag, so "unread" = PENDING (submitted, not yet actioned by an admin).
  const [pendingInquiries, setPendingInquiries] = useState<number | null>(null);

  // Fetch the pending count on load, then refresh every minute so the badge
  // stays live without a page reload. We only need the total, so limit=1.
  useEffect(() => {
    let active = true;
    const loadCount = async () => {
      try {
        const res = await inquiryService.list({ status: 'PENDING', limit: 1 });
        if (active) setPendingInquiries(res.meta.total);
      } catch {
        // If the count can't be fetched, leave the badge hidden rather than guess.
      }
    };
    loadCount();
    const timer = setInterval(loadCount, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const toggleMobileNavigationActive = useCallback(
    () => setMobileNavigationActive((active) => !active),
    []
  );

  const toggleUserMenuActive = useCallback(
    () => setUserMenuActive((active) => !active),
    []
  );

  const handleNavigationToggle = useCallback(() => {
    setMobileNavigationActive((active) => !active);
  }, []);

  const userMenuMarkup = (
    <TopBar.UserMenu
      actions={[
        {
          items: [
            { content: 'Settings', onAction: () => navigate('/settings') },
          ],
        },
        {
          items: [{ content: 'Log out', onAction: () => navigate('/login') }],
        },
      ]}
      name="Admin"
      initials="A"
      open={userMenuActive}
      onToggle={toggleUserMenuActive}
    />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      onNavigationToggle={handleNavigationToggle}
    />
  );

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={[
          {
            label: 'Dashboard',
            icon: HomeIcon,
            url: '/',
            matches: location.pathname === '/',
          },
          {
            label: 'Products',
            icon: ProductIcon,
            url: '/products',
            matches: location.pathname.startsWith('/products'),
          },
          {
            label: 'Categories',
            icon: CollectionIcon,
            url: '/categories',
            matches: location.pathname.startsWith('/categories'),
          },
          {
            label: 'Inquiries',
            icon: OrderIcon,
            url: '/inquiries',
            matches: location.pathname.startsWith('/inquiries'),
            // Live count of pending inquiries; hidden entirely when there are none.
            badge: pendingInquiries ? String(pendingInquiries) : undefined,
          },
          {
            label: 'Users',
            icon: PersonIcon,
            url: '/users',
            matches: location.pathname.startsWith('/users'),
          },
          {
            label: 'Content',
            icon: NoteIcon,
            url: '/content/contacts',
            matches: location.pathname.startsWith('/content'),
            subNavigationItems: [
              {
                label: 'Contacts',
                url: '/content/contacts',
              },
              {
                label: 'FAQs',
                url: '/content/faqs',
              },
              {
                label: 'Testimonials',
                url: '/content/testimonials',
              },
              {
                label: 'Banners',
                url: '/content/banners',
              },
              {
                label: 'Catalogues',
                url: '/content/catalogues',
              },
            ],
          },
        ]}
      />
      <Navigation.Section
        title="Settings"
        items={[
          {
            label: 'Settings',
            icon: SettingsIcon,
            url: '/settings',
            matches: location.pathname === '/settings',
          },
          {
            label: 'Logout',
            icon: ExitIcon,
            onClick: () => navigate('/login'),
          },
        ]}
      />
    </Navigation>
  );

  return (
    <Frame
      topBar={topBarMarkup}
      navigation={navigationMarkup}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigationActive}
    >
      <Outlet />
    </Frame>
  );
}
